import { useState } from 'react'
import { Button, InputNumber, message, Radio, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { generateFakeData } from '@/api/dataFactory'
import type { FakeDataItem, FakeDataType } from '@/types'

const { Text } = Typography

/** 虚拟数据类型选项（内置校验位算法：手机号段/身份证 GB11643/银行卡 Luhn/统一社会信用代码 MOD31-3） */
const TYPE_OPTIONS: { value: FakeDataType; label: string }[] = [
  { value: 'phone', label: '手机号' },
  { value: 'id_card', label: '身份证' },
  { value: 'name', label: '姓名' },
  { value: 'bank_card', label: '银行卡' },
  { value: 'email', label: '邮箱' },
  { value: 'address', label: '地址' },
  { value: 'company', label: '公司名称' },
  { value: 'uscc', label: '营业执照编号' },
]

/** 虚拟数据生成页签：随机生成 + 校验位校验，结果一键复制 */
export default function FakeDataTab() {
  const [dataType, setDataType] = useState<FakeDataType>('phone')
  const [count, setCount] = useState(10)
  const [items, setItems] = useState<FakeDataItem[]>([])
  const [loading, setLoading] = useState(false)

  /** 调用后端生成（校验位算法在后端统一实现） */
  const handleGenerate = async () => {
    setLoading(true)
    try {
      const res = await generateFakeData({ type: dataType, count })
      setItems(res.items)
      message.success(`已生成 ${res.items.length} 条${TYPE_OPTIONS.find((t) => t.value === dataType)?.label}`)
    } finally {
      setLoading(false)
    }
  }

  /** 复制全部生成值（换行分隔，可直接粘贴到用例参数表） */
  const handleCopyAll = async () => {
    await navigator.clipboard.writeText(items.map((it) => it.value).join('\n'))
    message.success('已复制全部数据')
  }

  const columns: ColumnsType<FakeDataItem> = [
    { title: '#', width: 60, render: (_, __, idx) => idx + 1 },
    {
      title: '生成值',
      dataIndex: 'value',
      render: (v: string) => (
        <Text copyable={{ text: v }} style={{ fontFamily: "'JetBrains Mono', 'Consolas', monospace" }}>{v}</Text>
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>校验</div>,
      width: 90,
      render: (_, record) => (
        <Tag color={record.valid ? 'success' : 'error'}>{record.valid ? '通过' : '未通过'}</Tag>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 工具栏：类型 / 数量 / 生成 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '0 22px 12px' }}>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          options={TYPE_OPTIONS}
          value={dataType}
          onChange={(e) => setDataType(e.target.value)}
        />
        <InputNumber
          min={1}
          max={200}
          value={count}
          onChange={(v) => setCount(v || 1)}
          addonBefore="数量"
          style={{ width: 140 }}
        />
        <Button
          type="primary"
          style={{ background: 'var(--au-primary)', border: 'none' }}
          loading={loading}
          onClick={handleGenerate}
        >
          生成
        </Button>
        <div style={{ flex: 1 }} />
        <Tooltip title="复制全部生成值（换行分隔）">
          <Button disabled={!items.length} onClick={handleCopyAll}>
            复制全部
          </Button>
        </Tooltip>
      </div>

      {/* 结果表 */}
      <Table
        size="small"
        columns={columns}
        dataSource={items}
        rowKey={(_, idx) => String(idx)}
        loading={loading}
        pagination={{ pageSize: 50, size: 'small', showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
        scroll={{ y: '100%' }}
        style={{ flex: 1, minHeight: 0 }}
      />
    </div>
  )
}
